import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslatorService } from '@app/services/translator/translator.service';
import { TranslateDirective, TranslatePipe } from '@ngx-translate/core';

export interface RangeSelection {
  start?: number;
  end?: number;
}

@Component({
  selector: 'app-range-select',
  templateUrl: './range-select.component.html',
  styleUrl: './range-select.component.css',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslatePipe, TranslateDirective],
})
export class RangeSelectComponent implements OnInit {
  private readonly translationService = inject(TranslatorService);
  @Input() id = 'rangeSelect';
  @Input() inclusive = false; // Include min/max as valid selections
  @Input() min = 0;
  @Input() max!: number;
  @Input() start!: number;
  @Input() end!: number;
  @Input() scale = 1;
  @Input() disabled = false;
  @Input() showLabels = false;
  @Input() labelFormat = '';
  @Input() labelFormatSingular = '';
  @Input() noStartLabel = '';
  @Input() noEndLabel = '';
  @Output() rangeChanged = new EventEmitter<RangeSelection>();
  @Output() rangeInput = new EventEmitter<RangeSelection>();
  startSignal = signal(0);
  endSignal = signal(0);
  endZIndex = signal(0);
  startZIndex = signal(1);
  startValue = computed(() => this.startSignal() * this.scale);
  endValue = computed(() => this.endSignal() * this.scale);
  minValue = computed(() => this.min * this.scale);
  maxValue = computed(() => this.max * this.scale);
  stepValue = computed(() => this.scale);
  endCSSClass = computed(() => this.getCSSClass(this.endSignal()));
  startCSSClass = computed(
    () => `startSlider ${this.getCSSClass(this.startSignal())}`
  );
  sliderBackground = computed(() => this.createRangeGradient());
  startSliderId = computed(() => `${this.id}_startSlider`);
  endSliderId = computed(() => `${this.id}_endSlider`);

  ngOnInit(): void {
    this.startSignal.set(this.start ?? this.min);
    this.endSignal.set(this.end ?? this.max);
  }

  startInput(e: Event): void {
    this.startZIndex.set(1);
    this.endZIndex.set(0);
    const value = this.parseInputEventValue(e);
    this.startSignal.set(value);
    if (value >= this.endSignal()) {
      this.endSignal.set(value);
    }
    this.emitInput();
  }

  startChange(e: Event): void {
    this.startInput(e);
    this.emitChange();
  }

  endInput(e: Event): void {
    this.endZIndex.set(1);
    this.startZIndex.set(0);
    const value = this.parseInputEventValue(e);
    this.endSignal.set(value);
    if (value <= this.startSignal()) {
      this.startSignal.set(value);
    }
    this.emitInput();
  }

  endChange(e: Event): void {
    this.endInput(e);
    this.emitChange();
  }

  startLabel() {
    return this.formatLabel(
      this.clampValue(this.startSignal()),
      this.noStartLabel
    );
  }
  endLabel() {
    return this.formatLabel(this.clampValue(this.endSignal()), this.noEndLabel);
  }
  minLabel() {
    return this.formatLabel(
      this.inclusive ? this.min : this.min + 1,
      this.noStartLabel
    );
  }
  maxLabel() {
    return this.formatLabel(
      this.inclusive ? this.max : this.max - 1,
      this.noEndLabel
    );
  }

  private emitInput(): void {
    const value = this.getRangeSelect();
    this.rangeInput.emit(value);
  }

  private emitChange(): void {
    const value = this.getRangeSelect();
    this.rangeChanged.emit(value);
  }

  private parseInputEventValue(e: Event) {
    return Number.parseInt((e.target as HTMLInputElement)?.value) / this.scale;
  }

  private clampValue(value?: number): number | undefined {
    if (this.inclusive) {
      return value;
    }
    return value > this.min && value < this.max ? value : undefined;
  }

  private createRangeGradient() {
    if (
      (!this.inclusive &&
        this.startSignal() == this.min &&
        this.endSignal() == this.max) ||
      this.startSignal() === this.endSignal()
    ) {
      return '';
    }
    const distance = this.max - this.min;
    const startPos = this.startSignal() - this.min;
    const endPos = this.endSignal() - this.min;
    const startPercent = (startPos / distance) * 100;
    const endPercent = (endPos / distance) * 100;

    return `linear-gradient(
      to right,
      #F5F8FC 1%,
      #F5F8FC ${startPercent}%,
      #1100DB ${startPercent}%,
      #1100DB ${endPercent}%,
      #F5F8FC ${endPercent}%,
      #F5F8FC 100%)`;
  }

  private getCSSClass(value?: number): string {
    return this.clampValue(value) !== undefined ? 'selected-range' : '';
  }

  private getRangeSelect(): RangeSelection {
    const start = this.clampValue(this.startValue());
    const end = this.clampValue(this.endValue());
    const value = { start, end };
    return value;
  }

  private formatLabel(value: number | undefined, defaultLabel: string): string {
    const format =
      value === 1 && this.labelFormatSingular
        ? this.labelFormatSingular
        : this.labelFormat;

    return value !== undefined
      ? this.translationService.translate(format, [value])
      : this.translationService.translate(defaultLabel);
  }
}
